import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import LeadCaptureModal from '../components/LeadCaptureModal';
import { pricingData } from '../data/extendedMock';

const Pricing = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        {/* Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
            {pricingData.headline}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            {pricingData.subheadline}
          </p>
          
          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-muted rounded-lg p-1">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                billingPeriod === 'monthly'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('annual')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                billingPeriod === 'annual'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Annual <span className="text-green-600 ml-1">Save 20%</span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {pricingData.plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-6 md:p-8 transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-primary text-primary-foreground shadow-2xl sm:scale-105 border-2 border-primary sm:col-span-2 lg:col-span-1'
                    : 'bg-card border border-border shadow-lg hover:shadow-xl'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-green-500 text-white text-sm font-semibold px-4 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className={`text-xl font-semibold mb-2 ${
                    plan.highlighted ? 'text-primary-foreground' : 'text-foreground'
                  }`}>
                    {plan.name}
                  </h3>
                  <div className="mb-2">
                    <span className={`text-4xl font-bold ${
                      plan.highlighted ? 'text-primary-foreground' : 'text-foreground'
                    }`}>
                      {billingPeriod === 'annual' && plan.price !== 'Custom'
                        ? `$${Math.round(parseInt(plan.price.replace('$', '')) * 0.8)}`
                        : plan.price}
                    </span>
                    <span className={`text-sm ml-2 ${
                      plan.highlighted ? 'text-primary-foreground/80' : 'text-muted-foreground'
                    }`}>
                      {plan.period}
                    </span>
                  </div>
                  <p className={`text-sm ${
                    plan.highlighted ? 'text-primary-foreground/80' : 'text-muted-foreground'
                  }`}>
                    {plan.description}
                  </p>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        plan.highlighted ? 'text-green-400' : 'text-green-600'
                      }`} />
                      <span className={`text-sm ${
                        plan.highlighted ? 'text-primary-foreground/90' : 'text-muted-foreground'
                      }`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => setIsModalOpen(true)}
                  variant={plan.highlighted ? 'secondary' : 'default'}
                  className="w-full"
                >
                  {plan.ctaText}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Link */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 text-center">
          <p className="text-muted-foreground">
            Have questions? Check out our{' '}
            <a href="/faq" className="text-primary hover:underline font-medium">
              FAQ page
            </a>{' '}
            or{' '}
            <a href="/contact" className="text-primary hover:underline font-medium">
              contact us
            </a>
            .
          </p>
        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />
      <LeadCaptureModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default Pricing;
